// deps
import React, { memo, useCallback, useState, useMemo } from "react";
import { ReactElement } from "react";

// internal-deps
import { HalfCard } from "@swiggy-private/dls-web-core/dist/components/HalfCard";
import { Text } from "@swiggy-private/dls-web-core/dist/components/Text";
import { AddressInfo } from "@swiggy-private/cart-types";
import { Image } from "@swiggy-private/dls-web-core/dist/components/Image";

// constants
import { BHARAT_MANDAPAM_IMAGE, BHARAT_MANDAPAM_TEXTS } from "./constants";
import { getPositionFromBottomForAddressList } from "@cart-ui/helpers/CartFooter";

// styles
import {
    SelectContainer,
    SelectTitle,
    PickupPointsList,
    PickupPointListItem,
    RadioCircle,
    PickupPointInfo,
    SelectButtonContainer,
    FullWidthSuperButton,
    NearestBadgeOrange,
    PickupPointNameRowLeft,
    ContentContainer,
    ConfirmImageContainer,
} from "./styles";

export interface BharatMandapamSelectPickupProps {
    isOpen: boolean;
    onClose: VoidFunction;
    addresses: AddressInfo[];
    onSelectAddress: (selectedAddress: AddressInfo, addressList: AddressInfo[]) => void;
}

const BharatMandapamSelectPickupContent = memo(function BharatMandapamSelectPickupContent({
    addresses,
    onSelectAddress,
}: Pick<BharatMandapamSelectPickupProps, "addresses" | "onSelectAddress">): ReactElement {
    const preSelectedAddress = useMemo(() => addresses.find((addr) => addr.isSelected) || addresses[0], [addresses]);
    const [selectedId, setSelectedId] = useState<string>(preSelectedAddress?.id || "");

    const handleSelectPickupPoint = useCallback((addressId: string) => {
        setSelectedId(addressId);
    }, []);

    const handleConfirm = useCallback(() => {
        const selected = addresses.find((addr) => addr.id === selectedId);
        if (selected) {
            onSelectAddress(selected, addresses);
        }
    }, [addresses, onSelectAddress, selectedId]);
    return (
        <SelectContainer>
            <ConfirmImageContainer>
                <Image
                    cloudinaryId={BHARAT_MANDAPAM_IMAGE.CLOUDINARY_ID}
                    height={`${BHARAT_MANDAPAM_IMAGE.HEIGHT}px`}
                    cloudinaryHeight={BHARAT_MANDAPAM_IMAGE.HEIGHT * 2}
                    fit="cover"
                    lazyLoad={false}
                />
            </ConfirmImageContainer>
            <ContentContainer>
                <SelectTitle variant="V2.heading2.neutral.bold" color="text_Highest_Emphasis">
                    {BHARAT_MANDAPAM_TEXTS.SELECT_YOUR_PICKUP_POINT}
                </SelectTitle>

                <PickupPointsList>
                    {addresses.map((address, index) => {
                        const isSelected = address.id === selectedId;
                        const isNearest = index === 0;
                        const pickupPointName =
                            address.annotation || `${BHARAT_MANDAPAM_TEXTS.PICKUP_POINT_LABEL} ${index + 1}`;
                        // const distance = (address as AddressInfo & { haversineDistance?: number }).haversineDistance;
                        // const getDistanceLabel = (d: number): string => {
                        //     if (d < 1000) return `${Math.trunc(d)} mtrs away`;
                        //     return `${(d / 1000).toFixed(1)} km away`;
                        // };

                        return (
                            <PickupPointListItem
                                key={address.id}
                                $isSelected={isSelected}
                                onClick={() => handleSelectPickupPoint(address.id)}
                                role="radio"
                                aria-checked={isSelected}
                            >
                                <RadioCircle $isSelected={isSelected} />
                                <PickupPointInfo>
                                    <PickupPointNameRowLeft>
                                        <Text
                                            variant={isSelected ? "V2.body2.neutral.bold" : "V2.body2.neutral.medium"}
                                            color={isSelected ? "text_Button_Secondary" : "overlay_Small_Heading_Text"}
                                        >
                                            {pickupPointName}
                                        </Text>
                                        {isNearest && (
                                            <NearestBadgeOrange
                                                isSelected={isSelected}
                                                variant="V2.caption.neutral.bold"
                                            >
                                                {BHARAT_MANDAPAM_TEXTS.NEAREST_BADGE}
                                            </NearestBadgeOrange>
                                        )}
                                    </PickupPointNameRowLeft>
                                    {/* {distanceLabel || pickupPointAddress ? (
                                        <Text
                                            variant="V2.body2.neutral.regular"
                                            color={isSelected ? "text_Button_Secondary" : "text_Low_Emphasis"}
                                            style={{ marginTop: "2px" }}
                                        >
                                            {distanceLabel && pickupPointAddress
                                                ? `${distanceLabel}, ${pickupPointAddress}`
                                                : distanceLabel || pickupPointAddress}
                                        </Text>
                                    ) : null} */}
                                </PickupPointInfo>
                            </PickupPointListItem>
                        );
                    })}
                </PickupPointsList>
            </ContentContainer>
            <SelectButtonContainer>
                <FullWidthSuperButton
                    onClick={handleConfirm}
                    buttonType="primary"
                    buttonSize="large"
                    variant="small"
                    role="button"
                >
                    {BHARAT_MANDAPAM_TEXTS.CONFIRM_PICKUP_POINT}
                </FullWidthSuperButton>
            </SelectButtonContainer>
        </SelectContainer>
    );
});

function BharatMandapamSelectPickup(props: BharatMandapamSelectPickupProps): ReactElement {
    const { isOpen, onClose, ...contentProps } = props;

    const handleClose = useCallback(() => {
        onClose();
    }, [onClose]);

    return (
        <HalfCard
            isOpen={isOpen}
            onClose={handleClose}
            closeOnOverlayClick
            hasCustomPadding={true}
            positionFromBottom={getPositionFromBottomForAddressList()}
        >
            <BharatMandapamSelectPickupContent {...contentProps} />
        </HalfCard>
    );
}

export default memo(BharatMandapamSelectPickup);
